import type { ImageAssetType } from '@/components/ui/carousel/carousel.types';
import { matchesAssetQuery } from '../property-asset-view/asset-images/assetSearch';
import { isFloorPlan } from '../floor-plan/floorPlan';

/**
 * Which photos a Photos-shaped tab is about (DV-3735): every photo (Photos), or
 * only the floor plans (Floor Plan). Unlike the other view filters it is decided
 * by the active tab, but it lives beside them in the imageAsset slice and
 * narrows every pool below the same way — so the Floor Plan tab's counts,
 * Select All and bulk actions can't reach a photo its grid isn't showing.
 * Absent means 'all'.
 */
export type PhotoPool = 'all' | 'floorPlans';

export const isInPool = (
  asset: ImageAssetType,
  pool: PhotoPool = 'all'
): boolean => pool === 'all' || isFloorPlan(asset);

/**
 * The view filters the Manage Assets grids apply: Show Hidden (DV-2221),
 * the DV-2856 search query and the DV-3735 tab pool. All narrow what is on
 * screen; none is a property of the photoset.
 */
export type AssetViewFilters = {
  showHidden: boolean;
  searchQuery: string;
  pool?: PhotoPool;
};

/**
 * What a pool needs when the search is the only filter that can narrow it —
 * the staged pools (Virtual Staging has no Show Hidden toggle) and every
 * checked-photo pool (Show Hidden parks photos, it never un-checks them).
 * A full `AssetViewFilters` satisfies this, so callers can pass either.
 */
export type AssetQueryFilter = Pick<AssetViewFilters, 'searchQuery' | 'pool'>;

/**
 * DV-2856 (post-review): the single place that decides what a view filter is
 * allowed to do to a selection.
 *
 * The rule, unchanged since DV-2221: a view filter narrows the "nothing is
 * checked" bulk default, and it narrows Select All — it never reaches past a
 * count into the action that count labels. The review on PR #1154 found three
 * bugs of exactly one shape: a count computed here and the action it labels
 * computed somewhere else, the two disagreeing under an active query. So every
 * selection-scoped pool is a LIST function below and every count is that
 * list's `.length` — a count and its action cannot drift apart without one of
 * them ceasing to compile.
 *
 * Product also made the conflicting state unreachable (2026-09-18): while
 * anything is checked, every view filter is locked (see `hasCheckedAssets`).
 * The lock is the guard; these functions are the invariant it protects.
 */

/**
 * Whether the current query and pool admit a photo — every view filter except
 * Show Hidden. Both read the caption, so a caption edit can take a photo out:
 * the slice's setCaption un-checks a photo that leaves this way, which is what
 * keeps a selection inside the view (see `hasCheckedAssets`).
 */
export const isInQueryScope = (
  asset: ImageAssetType,
  { searchQuery, pool }: AssetQueryFilter
): boolean => isInPool(asset, pool) && matchesAssetQuery(asset, searchQuery);

/** A photo the Photos grid is currently showing. */
export const isPhotoInView = (
  asset: ImageAssetType,
  filters: AssetViewFilters
): boolean =>
  (!asset.hidden || filters.showHidden) && isInQueryScope(asset, filters);

/** Every photo the Photos grid is currently showing. */
export const displayedPhotos = (
  assets: ImageAssetType[],
  filters: AssetViewFilters
): ImageAssetType[] => assets.filter((asset) => isPhotoInView(asset, filters));

/**
 * A staged photo the current query admits. The Virtual Staging grid layers the
 * role rule on top (internal roles also see hidden staged WIP — DV-2740 /
 * DV-2820); that is a permission, not a view filter, so it does not live here.
 */
export const isStagedAssetInView = (
  asset: ImageAssetType,
  { searchQuery }: AssetQueryFilter
): boolean => Boolean(asset.staged) && matchesAssetQuery(asset, searchQuery);

/** Every staged photo the current query admits (the tab's count label). */
export const stagedAssetsInView = (
  assets: ImageAssetType[],
  filters: AssetQueryFilter
): ImageAssetType[] =>
  assets.filter((asset) => isStagedAssetInView(asset, filters));

/**
 * A staged photo Virtual Staging's Select All and bulk actions operate on.
 * Hidden staged photos are excluded at every role: they are unapproved WIP,
 * and the download modal's include-hidden opt-in is the one way back in.
 */
export const isSelectableStagedAsset = (
  asset: ImageAssetType,
  filters: AssetQueryFilter
): boolean => isStagedAssetInView(asset, filters) && !asset.hidden;

/** Every staged photo Select All on Virtual Staging operates on. */
export const selectableStagedAssets = (
  assets: ImageAssetType[],
  filters: AssetQueryFilter
): ImageAssetType[] =>
  assets.filter((asset) => isSelectableStagedAsset(asset, filters));

/**
 * Every checked photo the current query and pool still admit — the root of all
 * three selection pools below.
 *
 * Hidden photos stay in: `showHidden` parks a photo out of the default bulk
 * pool, it never un-checks one (DV-2524). Each pool below re-applies whatever
 * hidden rule its own action has.
 */
const checkedInView = (
  assets: ImageAssetType[],
  filters: AssetQueryFilter
): ImageAssetType[] =>
  assets.filter((asset) => asset.isChecked && isInQueryScope(asset, filters));

/**
 * The pool every bulk action EXCEPT delete acts on: download, hide, services,
 * staging. Hidden photos are excluded (hidden is out of bulk scope — AC on
 * DV-2221).
 */
export const bulkSelection = (
  assets: ImageAssetType[],
  filters: AssetQueryFilter
): ImageAssetType[] =>
  checkedInView(assets, filters).filter((asset) => !asset.hidden);

/**
 * Trash's pool — the one bulk action that keeps hidden photos (DV-2524): a
 * hidden photo is a prime delete candidate, and it is only checkable with Show
 * Hidden on, so an explicit check must be honored. Drives both the selection
 * bar's count and the delete dialog's contents.
 */
export const deletableSelection = (
  assets: ImageAssetType[],
  filters: AssetQueryFilter
): ImageAssetType[] => checkedInView(assets, filters);

/**
 * Whether Stage with AI may take this photo at all, from its card or in a
 * batch (DV-3735): a floor plan is a drawing, not a room to furnish. Decided
 * here rather than per tab, so no tab offers it: every grid that shows a Stage
 * or Regenerate button asks this (AssetsImageGrid, StagedAssetsGrid).
 */
export const canStagePhoto = (asset: ImageAssetType): boolean =>
  !isFloorPlan(asset);

/**
 * Batch staging's pool: selected photos that can still be staged (a staged
 * result is not re-stageable from here). Drives both the Stage with AI
 * button's enablement and the photos the staging modal bills for.
 */
export const stageableSelection = (
  assets: ImageAssetType[],
  filters: AssetQueryFilter
): ImageAssetType[] =>
  bulkSelection(assets, filters).filter(
    (asset) => !asset.staged && canStagePhoto(asset)
  );

/**
 * Whether the view filters are locked (decided 2026-09-18). Selection is
 * per-tab-visit — switching tabs clears every checkmark — so "anything checked"
 * always means "anything checked on the tab you are looking at", and one
 * expression covers both Photos and Virtual Staging.
 *
 * Deliberately NOT narrowed by the query: a selection the current query hides
 * must still hold the filters, or releasing the lock would be the very edit
 * that strands it.
 */
export const hasCheckedAssets = (assets: ImageAssetType[]): boolean =>
  assets.some((asset) => asset.isChecked);

/**
 * The one explanation every locked control shows. Tim's prototype string minus
 * "or use More Actions", which no longer applies — the bulk actions left the
 * kebab for the selection bar in August.
 */
export const FILTER_LOCK_TOOLTIP = 'Clear your selection to change filters';
